import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import LeagueService from '../services/LeagueService';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple } from '../utils/vfx';
import {
  FaUsers,
  FaChartBar,
  FaPlus,
  FaGraduationCap,
  FaUserPlus,
  FaArrowLeft,
  FaEye,
  FaTrash,
  FaUserShield,
  FaCheckCircle,
  FaTimesCircle,
  FaBook,
  FaClipboardCheck,
  FaEdit,
  FaSave,
  FaTrophy,
  FaCalendar,
  FaFilePdf,
  FaArrowUp,
  FaArrowDown,
  FaGripVertical,
  FaTimes,
  FaQuestion,
  FaTachometerAlt,
  FaMedal
} from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import TeacherCurriculumService from '../services/TeacherCurriculumService';
import LeagueChallengesService from '../services/LeagueChallengesService';
import RichTextEditor from './RichTextEditor';
import PdfLessonUploader from './PdfLessonUploader';
import './LeagueManagement.css';

ChartJS.register(
  CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, ArcElement,
  Tooltip, Legend, Filler
);

const LeagueManagement = () => {
  const navigate = useNavigate();
  const { state } = useUser();
  const [loading, setLoading] = useState(true);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentAnalytics, setStudentAnalytics] = useState(null);
  const [showCreateStudent, setShowCreateStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({ username: '', password: '' });
  const [activeView, setActiveView] = useState('leagues'); // 'leagues', 'analytics', 'students', 'student-analytics', 'assignments', 'curriculum', 'challenges'
  const [assignments, setAssignments] = useState({ lessons: [], quizzes: [] });
  const [availableAssignments, setAvailableAssignments] = useState({ lessons: [], quizzes: [] });
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignType, setAssignType] = useState('lesson'); // 'lesson' or 'quiz'
  const [selectedAssignId, setSelectedAssignId] = useState(null);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  
  // Curriculum state
  const [curriculumLessons, setCurriculumLessons] = useState([]);
  const [curriculumQuizzes, setCurriculumQuizzes] = useState([]);
  const [loadingCurriculum, setLoadingCurriculum] = useState(false);
  const [showCreateLesson, setShowCreateLesson] = useState(false);
  const [showCreateQuiz, setShowCreateQuiz] = useState(false);
  const [showPdfUploader, setShowPdfUploader] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [editingQuizQuestions, setEditingQuizQuestions] = useState(null); // Quiz ID for in-depth question editing
  const [lessonForm, setLessonForm] = useState({ title: '', content: '', order: 0, xpValue: 50, isActive: true });
  const [quizForm, setQuizForm] = useState({ lessonId: '', title: '', description: '', type: 'multiple_choice', questions: [], timeLimit: '', passingScore: 70, xpValue: 25, isActive: true });
  const [editableQuestions, setEditableQuestions] = useState([]); // Questions being edited in-depth
  
  // Challenges state
  const [challenges, setChallenges] = useState([]);
  const [loadingChallenges, setLoadingChallenges] = useState(false);
  const [showCreateChallenge, setShowCreateChallenge] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [challengeForm, setChallengeForm] = useState({ title: '', description: '', startDate: '', endDate: '', startingBalance: 10000, maxParticipants: '', prizePool: '', rules: null, isActive: false, isPublished: false });
  
  // Join league state
  const [showJoinLeague, setShowJoinLeague] = useState(false);
  const [leagueCode, setLeagueCode] = useState('');

  // Teacher KPIs state
  const [teacherKpis, setTeacherKpis] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teachersList, setTeachersList] = useState([]);
  const [loadingKpis, setLoadingKpis] = useState(false);

  const isAdmin = state.user?.isAdmin || 
                  state.user?.adminLevel === '1' || 
                  state.user?.adminLevel === '2' || 
                  state.user?.adminLevel === '3' || 
                  state.user?.adminLevel === 'master_admin';
  const isTeacher = state.user?.isTeacher === true;

  useEffect(() => {
    if (!state.user || (!isAdmin && !isTeacher)) {
      toast.error('Access denied');
      navigate('/dashboard');
      return;
    }

    soundEffects.playPageTransition();
    loadLeagues();
  }, [state.user, navigate, isAdmin, isTeacher]);

  const loadLeagues = async () => {
    try {
      setLoading(true);
      console.log('[LeagueManagement] Loading leagues for user:', { 
        userId: state.user?.id, 
        isTeacher, 
        isAdmin 
      });
      const data = await LeagueService.getLeagues();
      console.log('[LeagueManagement] Leagues loaded:', data);
      setLeagues(data.leagues || []);
      
      // If teacher has no leagues, show helpful message
      if (isTeacher && !isAdmin && (data.leagues || []).length === 0) {
        toast.info('You are not assigned to any leagues yet. Please contact an admin to be assigned to a league.');
      }
    } catch (error) {
      console.error('[LeagueManagement] Failed to load leagues:', error);
      const errorMessage = error.error || error.message || 'Failed to load leagues';
      toast.error(errorMessage);
      
      // If it's a 403 error, the teacher might not have the isTeacher flag set
      if (error.status === 403 || (error.response && error.response.status === 403)) {
        toast.error('Access denied. Please ensure your teacher role is properly set.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadLeagueDetails = async (leagueId) => {
    try {
      const data = await LeagueService.getLeague(leagueId);
      setSelectedLeague(data.league);
      return data.league;
    } catch (error) {
      toast.error(error.error || 'Failed to load league details');
      return null;
    }
  };

  const loadAnalytics = async (leagueId) => {
    try {
      const data = await LeagueService.getLeagueAnalytics(leagueId);
      setAnalytics(data.analytics);
    } catch (error) {
      toast.error(error.error || 'Failed to load analytics');
    }
  };

  const loadStudentAnalytics = async (studentId) => {
    try {
      const data = await LeagueService.getStudentAnalytics(studentId);
      setStudentAnalytics(data);
    } catch (error) {
      toast.error(error.error || 'Failed to load student analytics');
    }
  };

  const handleViewStudentAnalytics = async (student) => {
    soundEffects.playClick();
    setSelectedStudent(student);
    setActiveView('student-analytics');
    await loadStudentAnalytics(student.id);
  };

  const loadTeacherKpis = async (teacherId) => {
    try {
      setLoadingKpis(true);
      const data = await LeagueService.getTeacherKPIs(teacherId);
      setTeacherKpis(data);
      setSelectedTeacher(data.teacher || null);
    } catch (error) {
      toast.error(error.error || 'Failed to load teacher KPIs');
    } finally {
      setLoadingKpis(false);
    }
  };

  const handleViewTeacherKpis = async () => {
    soundEffects.playClick();
    if (isAdmin) {
      // Admin: show teacher picker
      try {
        const data = await LeagueService.getTeachers();
        setTeachersList(data.teachers || []);
        setActiveView('teachers-list');
      } catch (error) {
        toast.error(error.error || 'Failed to load teachers list');
      }
    } else {
      // Teacher: load own KPIs directly
      setActiveView('teacher-kpis');
      await loadTeacherKpis('me');
    }
  };

  const handleViewSpecificTeacherKpis = async (teacher) => {
    soundEffects.playClick();
    setActiveView('teacher-kpis');
    await loadTeacherKpis(teacher.id);
  };

  const loadAssignments = async (leagueId) => {
    try {
      setLoadingAssignments(true);
      const data = await LeagueService.getLeagueAssignments(leagueId);
      setAssignments({
        lessons: data.lessons || [],
        quizzes: data.quizzes || []
      });
    } catch (error) {
      toast.error(error.error || 'Failed to load assignments');
    } finally {
      setLoadingAssignments(false);
    }
  };

  const loadAvailableAssignments = async (leagueId = null) => {
    try {
      const data = await LeagueService.getAvailableAssignments(leagueId);
      setAvailableAssignments({
        lessons: data.lessons || [],
        quizzes: data.quizzes || []
      });
    } catch (error) {
      toast.error(error.error || 'Failed to load available assignments');
    }
  };

  // Organize assignments into hierarchical structure
  const organizeAssignments = (items, isQuiz = false) => {
    const coursesMap = new Map();

    items.forEach(item => {
      const courseId = item.courseId || 'uncategorized';
      const courseTitle = item.courseTitle || (item.source === 'teacher' ? 'Teacher Curriculum' : 'Uncategorized');
      const unitId = item.unitId || 'uncategorized';
      const unitTitle = item.unitTitle || 'Uncategorized';

      // Get or create course
      if (!coursesMap.has(courseId)) {
        coursesMap.set(courseId, {
          id: courseId,
          title: courseTitle,
          units: new Map()
        });
      }
      const course = coursesMap.get(courseId);

      // Get or create unit
      if (!course.units.has(unitId)) {
        course.units.set(unitId, {
          id: unitId,
          title: unitTitle,
          items: []
        });
      }
      const unit = course.units.get(unitId);

      // For quizzes, organize by lesson
      if (isQuiz && item.lessonId) {
        const lessonId = item.lessonId;
        const lessonTitle = item.lessonTitle;
        
        // Check if lesson already exists in unit
        let lessonGroup = unit.items.find(l => l.type === 'lesson' && l.id === lessonId);
        if (!lessonGroup) {
          lessonGroup = {
            type: 'lesson',
            id: lessonId,
            title: lessonTitle,
            quizzes: []
          };
          unit.items.push(lessonGroup);
        }
        lessonGroup.quizzes.push({ ...item, source: item.source || 'global' });
      } else if (isQuiz && !item.lessonId) {
        // Handle edge case: quiz without lessonId (shouldn't happen but handle it)
        unit.items.push({
          type: 'quiz',
          ...item,
          source: item.source || 'global'
        });
      } else {
        // For lessons, add directly
        unit.items.push({
          type: 'lesson',
          ...item,
          source: item.source || 'global'
        });
      }
    });

    // Convert Maps to arrays and sort
    return Array.from(coursesMap.values())
      .sort((a, b) => a.id - b.id)
      .map(course => ({
        ...course,
        units: Array.from(course.units.values())
          .sort((a, b) => a.id - b.id)
          .map(unit => ({
            ...unit,
            items: unit.items.map(item => {
              // Sort quizzes within lesson groups
              if (item.type === 'lesson' && item.quizzes) {
                return {
                  ...item,
                  quizzes: item.quizzes.sort((a, b) => a.id - b.id)
                };
              }
              return item;
            }).sort((a, b) => {
              // Sort lessons/quizzes by their id
              if (a.type === 'lesson' && b.type === 'lesson') {
                return a.id - b.id;
              }
              if (a.type === 'quiz' && b.type === 'quiz') {
                return a.id - b.id;
              }
              return 0;
            })
          }))
      }));
  };

  const handleAssign = async () => {
    if (!selectedAssignId) {
      toast.error('Please select a course, unit, lesson, or quiz to assign');
      return;
    }

    try {
      // Check if it's a course, unit, lesson, or quiz selection
      if (selectedAssignId.startsWith('course-')) {
        const courseId = parseInt(selectedAssignId.split('-')[1]);
        const result = await LeagueService.bulkAssignToLeague(selectedLeague.id, {
          courseId,
          assignType
        });
        toast.success(
          `Successfully assigned ${result.assigned} ${assignType}${result.assigned !== 1 ? 's' : ''}${result.skipped > 0 ? ` (${result.skipped} already assigned)` : ''}`
        );
      } else if (selectedAssignId.startsWith('unit-')) {
        const parts = selectedAssignId.split('-');
        const unitId = parseInt(parts[2]);
        const result = await LeagueService.bulkAssignToLeague(selectedLeague.id, {
          unitId,
          assignType
        });
        toast.success(
          `Successfully assigned ${result.assigned} ${assignType}${result.assigned !== 1 ? 's' : ''}${result.skipped > 0 ? ` (${result.skipped} already assigned)` : ''}`
        );
      } else {
        // Individual lesson or quiz
        if (assignType === 'lesson') {
          await LeagueService.assignToLeague(selectedLeague.id, { lessonId: selectedAssignId });
        } else {
          await LeagueService.assignToLeague(selectedLeague.id, { quizId: selectedAssignId });
        }
        toast.success(`${assignType === 'lesson' ? 'Lesson' : 'Quiz'} assigned successfully`);
      }
      setShowAssignModal(false);
      setSelectedAssignId(null);
      await loadAssignments(selectedLeague.id);
    } catch (error) {
      toast.error(error.error || 'Failed to assign');
    }
  };

  const handleRemoveAssignment = async (lessonId, quizId) => {
    try {
      await LeagueService.removeAssignment(selectedLeague.id, { lessonId, quizId });
      toast.success('Assignment removed successfully');
      await loadAssignments(selectedLeague.id);
    } catch (error) {
      toast.error(error.error || 'Failed to remove assignment');
    }
  };

  // Curriculum functions
  const loadCurriculum = async (leagueId) => {
    try {
      setLoadingCurriculum(true);
      // Load both curriculum and assignments in parallel
      const [lessonsData, quizzesData] = await Promise.all([
        TeacherCurriculumService.getLessons(leagueId),
        TeacherCurriculumService.getQuizzes(leagueId),
        loadAssignments(leagueId) // Also load assignments to check which are assigned
      ]);
      setCurriculumLessons(lessonsData.lessons || []);
      setCurriculumQuizzes(quizzesData.quizzes || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load curriculum');
    } finally {
      setLoadingCurriculum(false);
    }
  };

  const handleCreateLesson = async () => {
    if (!selectedLeague) return;
    try {
      await TeacherCurriculumService.createLesson(selectedLeague.id, lessonForm);
      toast.success('Lesson created successfully');
      setShowCreateLesson(false);
      setLessonForm({ title: '', content: '', order: 0, xpValue: 50, isActive: true });
      await loadCurriculum(selectedLeague.id);
    } catch (error) {
      toast.error(error.message || 'Failed to create lesson');
    }
  };

  const handleUpdateLesson = async (lessonId) => {
    if (!selectedLeague) return;
    try {
      await TeacherCurriculumService.updateLesson(selectedLeague.id, lessonId, lessonForm);
      toast.success('Lesson updated successfully');
      setEditingLesson(null);
      setLessonForm({ title: '', content: '', order: 0, xpValue: 50, isActive: true });
      await loadCurriculum(selectedLeague.id);
    } catch (error) {
      toast.error(error.message || 'Failed to update lesson');
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!selectedLeague) return;
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;
    try {
      await TeacherCurriculumService.deleteLesson(selectedLeague.id, lessonId);
      toast.success('Lesson deleted successfully');
      await loadCurriculum(selectedLeague.id);
    } catch (error) {
      toast.error(error.message || 'Failed to delete lesson');
    }
  };

  const handleCreateQuiz = async () => {
    if (!selectedLeague) return;
    try {
      await TeacherCurriculumService.createQuiz(selectedLeague.id, quizForm);
      toast.success('Quiz created successfully');
      setShowCreateQuiz(false);
      setQuizForm({ lessonId: '', title: '', description: '', type: 'multiple_choice', questions: [], timeLimit: '', passingScore: 70, xpValue: 25, isActive: true });
      await loadCurriculum(selectedLeague.id);
    } catch (error) {
      toast.error(error.message || 'Failed to create quiz');
    }
  };

  const handleUpdateQuiz = async (quizId) => {
    if (!selectedLeague) return;
    try {
      await TeacherCurriculumService.updateQuiz(selectedLeague.id, quizId, quizForm);
      toast.success('Quiz updated successfully');
      setEditingQuiz(null);
      setQuizForm({ lessonId: '', title: '', description: '', type: 'multiple_choice', questions: [], timeLimit: '', passingScore: 70, xpValue: 25, isActive: true });
      await loadCurriculum(selectedLeague.id);
    } catch (error) {
      toast.error(error.message || 'Failed to update quiz');
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!selectedLeague) return;
    if (!window.confirm('Are you sure you want to delete this quiz?')) return;
    try {
      await TeacherCurriculumService.deleteQuiz(selectedLeague.id, quizId);
      toast.success('Quiz deleted successfully');
      await loadCurriculum(selectedLeague.id);
    } catch (error) {
      toast.error(error.message || 'Failed to delete quiz');
    }
  };

  // Quiz Questions Editing Functions
  const handleStartEditQuestions = (quiz) => {
    soundEffects.playClick();
    const quizQuestions = Array.isArray(quiz.questions) 
      ? quiz.questions 
      : (typeof quiz.questions === 'string' ? JSON.parse(quiz.questions || '[]') : []);
    
    setEditableQuestions(quizQuestions.map((q, idx) => {
      const question = { ...q, id: q.id || idx + 1 };
      if (q.type === 'short_answer') {
        question.referenceAnswer = q.referenceAnswer || q.correctAnswer || '';
      }
      if (q.type === 'multiple_choice') {
        question.options = Array.isArray(q.options) ? q.options.map(opt => opt || '') : ['', ''];
      }
      return question;
    }));
    setEditingQuizQuestions(quiz.id);
    setQuizForm({
      lessonId: quiz.lessonId || '',
      title: quiz.title || '',
      description: quiz.description || '',
      type: quiz.type || 'multiple_choice',
      questions: quizQuestions,
      timeLimit: quiz.timeLimit || '',
      passingScore: quiz.passingScore || 70,
      xpValue: quiz.xpValue || 25,
      isActive: quiz.isActive !== undefined ? quiz.isActive : true
    });
  };

  const handleCancelEditQuestions = () => {
    soundEffects.playClick();
    setEditingQuizQuestions(null);
    setEditableQuestions([]);
    setQuizForm({ lessonId: '', title: '', description: '', type: 'multiple_choice', questions: [], timeLimit: '', passingScore: 70, xpValue: 25, isActive: true });
  };

  const handleSaveQuizQuestions = async () => {
    if (!selectedLeague || !editingQuizQuestions) return;
    try {
      soundEffects.playClick();
      
      // Validate questions
      for (let i = 0; i < editableQuestions.length; i++) {
        const q = editableQuestions[i];
        if (!q.question || !q.question.trim()) {
          toast.error(`Question ${i + 1} cannot be empty`);
          return;
        }
        if (!q.type) {
          toast.error(`Question ${i + 1} must have a type`);
          return;
        }
        if (q.type === 'multiple_choice' && (!q.options || q.options.length < 2)) {
          toast.error(`Question ${i + 1} (multiple choice) must have at least 2 options`);
          return;
        }
        if (q.type === 'short_answer' && !q.referenceAnswer && !q.correctAnswer) {
          toast.error(`Question ${i + 1} (short answer) must have a reference answer`);
          return;
        }
      }

      const questionsToSave = editableQuestions.map(q => {
        if (q.type === 'short_answer') {
          const questionData = { ...q, referenceAnswer: q.referenceAnswer || q.correctAnswer || '' };
          if (questionData.referenceAnswer) delete questionData.correctAnswer;
          return questionData;
        }
        return q;
      });

      await TeacherCurriculumService.updateQuiz(selectedLeague.id, editingQuizQuestions, {
        ...quizForm,
        questions: questionsToSave
      });
      
      toast.success('Quiz questions updated successfully');
      setEditingQuizQuestions(null);
      setEditableQuestions([]);
      setQuizForm({ lessonId: '', title: '', description: '', type: 'multiple_choice', questions: [], timeLimit: '', passingScore: 70, xpValue: 25, isActive: true });
      await loadCurriculum(selectedLeague.id);
    } catch (error) {
      console.error('Error updating quiz questions:', error);
      toast.error(error.message || 'Failed to update quiz questions');
    }
  };

  const handleAddQuizQuestion = () => {
    const newQuestion = {
      id: editableQuestions.length > 0 ? Math.max(...editableQuestions.map(q => q.id || 0)) + 1 : 1,
      type: 'multiple_choice',
      question: '',
      options: ['', ''],
      correctAnswer: 0,
      explanation: ''
    };
    setEditableQuestions([...editableQuestions, newQuestion]);
  };

  const handleDeleteQuizQuestion = (index) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      setEditableQuestions(editableQuestions.filter((_, i) => i !== index));
    }
  };

  const handleQuizQuestionChange = (index, field, value) => {
    const updated = [...editableQuestions];
    updated[index] = { ...updated[index], [field]: value };
    
    if (field === 'type') {
      if (value === 'multiple_choice') {
        updated[index] = {
          ...updated[index],
          options: updated[index].options || ['', ''],
          correctAnswer: updated[index].correctAnswer !== undefined ? updated[index].correctAnswer : 0,
          referenceAnswer: undefined
        };
      } else if (value === 'true_false') {
        updated[index] = {
          ...updated[index],
          options: undefined,
          correctAnswer: typeof updated[index].correctAnswer === 'boolean' ? updated[index].correctAnswer : true,
          referenceAnswer: undefined
        };
      } else if (value === 'short_answer') {
        updated[index] = {
          ...updated[index],
          options: undefined,
          correctAnswer: undefined,
          referenceAnswer: updated[index].referenceAnswer || updated[index].correctAnswer || ''
        };
      }
    }
    
    if (field === 'correctAnswer' && updated[index].type === 'multiple_choice') {
      updated[index].correctAnswer = parseInt(value) || 0;
    }
    
    setEditableQuestions(updated);
  };

  const handleQuizOptionChange = (questionIndex, optionIndex, value) => {
    const updated = [...editableQuestions];
    if (!updated[questionIndex].options) {
      updated[questionIndex].options = [];
    }
    updated[questionIndex].options[optionIndex] = value;
    setEditableQuestions(updated);
  };

  const handleAddQuizOption = (questionIndex) => {
    const updated = [...editableQuestions];
    if (!updated[questionIndex].options) {
      updated[questionIndex].options = [];
    }
    updated[questionIndex].options.push('');
    setEditableQuestions(updated);
  };

  const handleRemoveQuizOption = (questionIndex, optionIndex) => {
    const updated = [...editableQuestions];
    if (updated[questionIndex].options.length > 2) {
      updated[questionIndex].options.splice(optionIndex, 1);
      if (updated[questionIndex].correctAnswer >= optionIndex) {
        updated[questionIndex].correctAnswer = Math.max(0, updated[questionIndex].correctAnswer - 1);
      }
      setEditableQuestions(updated);
    } else {
      toast.error('Multiple choice questions must have at least 2 options');
    }
  };

  const handleMoveQuizQuestion = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === editableQuestions.length - 1)) {
      return;
    }
    const updated = [...editableQuestions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setEditableQuestions(updated);
  };

  // Challenges functions
  const loadChallenges = async (leagueId) => {
    try {
      setLoadingChallenges(true);
      const data = await LeagueChallengesService.getChallenges(leagueId);
      setChallenges(data.challenges || []);
    } catch (error) {
      toast.error(error.error || 'Failed to load challenges');
    } finally {
      setLoadingChallenges(false);
    }
  };

  const handleCreateChallenge = async () => {
    if (!selectedLeague) return;
    try {
      await LeagueChallengesService.createChallenge(selectedLeague.id, challengeForm);
      toast.success('Challenge created successfully');
      setShowCreateChallenge(false);
      setChallengeForm({ title: '', description: '', startDate: '', endDate: '', startingBalance: 10000, maxParticipants: '', prizePool: '', rules: null, isActive: false, isPublished: false });
      await loadChallenges(selectedLeague.id);
    } catch (error) {
      toast.error(error.error || 'Failed to create challenge');
    }
  };

  const handleUpdateChallenge = async (challengeId) => {
    if (!selectedLeague) return;
    try {
      await LeagueChallengesService.updateChallenge(selectedLeague.id, challengeId, challengeForm);
      toast.success('Challenge updated successfully');
      setEditingChallenge(null);
      setChallengeForm({ title: '', description: '', startDate: '', endDate: '', startingBalance: 10000, maxParticipants: '', prizePool: '', rules: null, isActive: false, isPublished: false });
      await loadChallenges(selectedLeague.id);
    } catch (error) {
      toast.error(error.error || 'Failed to update challenge');
    }
  };

  const handleDeleteChallenge = async (challengeId) => {
    if (!selectedLeague) return;
    if (!window.confirm('Are you sure you want to delete this challenge?')) return;
    try {
      await LeagueChallengesService.deleteChallenge(selectedLeague.id, challengeId);
      toast.success('Challenge deleted successfully');
      await loadChallenges(selectedLeague.id);
    } catch (error) {
      toast.error(error.error || 'Failed to delete challenge');
    }
  };

  const handleJoinLeague = async (e) => {
    e.preventDefault();
    if (!leagueCode.trim()) {
      toast.error('Please enter a league code');
      return;
    }

    try {
      await LeagueService.joinLeague(leagueCode.trim());
      toast.success('Successfully joined league!');
      setShowJoinLeague(false);
      setLeagueCode('');
      await loadLeagues();
    } catch (error) {
      toast.error(error.error || 'Failed to join league');
    }
  };

  const handleViewLeague = async (league) => {
    soundEffects.playClick();
    const leagueData = await loadLeagueDetails(league.id);
    if (leagueData) {
      setSelectedLeague(leagueData);
      setActiveView('students');
    }
  };

  const handleViewAnalytics = async (league) => {
    soundEffects.playClick();
    const leagueData = await loadLeagueDetails(league.id);
    if (leagueData) {
      setSelectedLeague(leagueData);
      setActiveView('analytics');
      await loadAnalytics(league.id);
    }
  };

  const handleCreateStudent = async (e) => {
    e.preventDefault();
    if (!selectedLeague) return;

    try {
      await LeagueService.createStudent(selectedLeague.id, newStudent);
      toast.success('Student account created successfully!');
      setNewStudent({ username: '', password: '' });
      setShowCreateStudent(false);
      await loadLeagueDetails(selectedLeague.id);
    } catch (error) {
      toast.error(error.error || 'Failed to create student account');
    }
  };

  if (!state.user || (!isAdmin && !isTeacher)) {
    return null;
  }

  return (
    <div className="league-management">
      <div className="league-header">
        <button 
          className="back-button" 
          onClick={(e) => {
            soundEffects.playClick();
            createRipple(e);
            navigate('/dashboard');
          }}
          onMouseEnter={() => soundEffects.playHover()}
        >
          <FaArrowLeft />
        </button>
        <h1>League Management</h1>
        <div className="user-info">
          {isAdmin && <FaUserShield />}
          {isTeacher && <FaGraduationCap />}
          {state.user.name || state.user.email}
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : activeView === 'leagues' ? (
        <div className="leagues-list">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <h2>Your Leagues</h2>
            <button
              className="action-btn analytics-btn"
              onClick={handleViewTeacherKpis}
              onMouseEnter={() => soundEffects.playHover()}
              title={isAdmin ? 'View KPIs for any teacher' : 'View your teacher KPIs'}
            >
              <FaTachometerAlt /> {isAdmin ? 'Teacher KPIs' : 'My KPIs'}
            </button>
          </div>
          {leagues.length === 0 ? (
            <div className="empty-state">
              <FaUsers size={48} />
              <p>No leagues found. {isAdmin && 'Create a league in the Admin Panel.'}</p>
              {isTeacher && !isAdmin && (
                <div style={{ marginTop: '20px' }}>
                  <p style={{ marginBottom: '10px' }}>
                    You are not assigned to any leagues yet. Please contact an admin to be assigned to a league, or join one using a league code below.
                  </p>
                  {!showJoinLeague ? (
                    <button
                      className="action-btn view-btn"
                      onClick={() => {
                        soundEffects.playClick();
                        setShowJoinLeague(true);
                      }}
                    >
                      <FaUserPlus /> Join League
                    </button>
                  ) : (
                    <motion.form
                      onSubmit={handleJoinLeague}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '300px', margin: '0 auto' }}
                    >
                      <input
                        type="text"
                        value={leagueCode}
                        onChange={(e) => setLeagueCode(e.target.value.toUpperCase())}
                        placeholder="Enter league code"
                        style={{
                          padding: '10px',
                          fontSize: '16px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          textAlign: 'center',
                          letterSpacing: '2px',
                          textTransform: 'uppercase'
                        }}
                        maxLength={6}
                      />
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                        <button
                          type="submit"
                          className="action-btn view-btn"
                          style={{ flex: 1 }}
                        >
                          <FaCheckCircle /> Join
                        </button>
                        <button
                          type="button"
                          className="action-btn analytics-btn"
                          onClick={() => {
                            setShowJoinLeague(false);
                            setLeagueCode('');
                          }}
                          style={{ flex: 1 }}
                        >
                          <FaTimesCircle /> Cancel
                        </button>
                      </div>
                    </motion.form>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="leagues-grid">
              {leagues.map((league) => (
                <motion.div
                  key={league.id}
                  className="league-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.02 }}
                  onClick={(e) => {
                    soundEffects.playClick();
                    createRipple(e);
                    handleViewLeague(league);
                  }}
                >
                  <div className="league-card-header">
                    <h3>{league.name}</h3>
                    <span className="league-code">{league.code}</span>
                  </div>
                  {league.description && (
                    <p className="league-description">{league.description}</p>
                  )}
                  <div className="league-stats">
                    <div className="stat">
                      <FaUsers />
                      <span>{league.student_count || 0} Students</span>
                    </div>
                    <div className="stat">
                      <FaGraduationCap />
                      <span>{league.teacher_count || 0} Teachers</span>
                    </div>
                  </div>
                  <div className="league-actions">
                    <button
                      className="action-btn view-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        soundEffects.playClick();
                        handleViewLeague(league);
                      }}
                    >
                      <FaEye /> View Students
                    </button>
                    <button
                      className="action-btn analytics-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        soundEffects.playClick();
                        handleViewAnalytics(league);
                      }}
                    >
                      <FaChartBar /> Analytics
                    </button>
                    <button
                      className="action-btn assignments-btn"
                      onClick={async (e) => {
                        e.stopPropagation();
                        soundEffects.playClick();
                        const leagueData = await loadLeagueDetails(league.id);
                        if (leagueData) {
                          setSelectedLeague(leagueData);
                          setActiveView('assignments');
                          await loadAssignments(league.id);
                          await loadAvailableAssignments();
                        }
                      }}
                    >
                      <FaGraduationCap /> Assignments
                    </button>
                    <button
                      className="action-btn curriculum-btn"
                      onClick={async (e) => {
                        e.stopPropagation();
                        soundEffects.playClick();
                        const leagueData = await loadLeagueDetails(league.id);
                        if (leagueData) {
                          setSelectedLeague(leagueData);
                          setActiveView('curriculum');
                          await loadCurriculum(league.id);
                        }
                      }}
                    >
                      <FaBook /> Curriculum
                    </button>
                    <button
                      className="action-btn challenges-btn"
                      onClick={async (e) => {
                        e.stopPropagation();
                        soundEffects.playClick();
                        const leagueData = await loadLeagueDetails(league.id);
                        if (leagueData) {
                          setSelectedLeague(leagueData);
                          setActiveView('challenges');
                          await loadChallenges(league.id);
                        }
                      }}
                    >
                      <FaTrophy /> Challenges
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      ) : activeView === 'students' && selectedLeague ? (
        <div className="league-details">
          <div className="details-header">
            <button
              className="back-btn"
              onClick={() => {
                soundEffects.playClick();
                setActiveView('leagues');
                setSelectedLeague(null);
              }}
            >
              <FaArrowLeft />
            </button>
            <h2>{selectedLeague.name}</h2>
            <span className="league-code">{selectedLeague.code}</span>
          </div>

          <div className="details-actions">
            <button
              className="create-student-btn"
              onClick={() => {
                soundEffects.playClick();
                setShowCreateStudent(true);
              }}
            >
              <FaUserPlus /> Create Student Account
            </button>
            <button
              className="analytics-btn"
              onClick={async () => {
                soundEffects.playClick();
                setActiveView('analytics');
                await loadAnalytics(selectedLeague.id);
              }}
            >
              <FaChartBar /> View Analytics
            </button>
            <button
              className="assignments-btn"
              onClick={async () => {
                soundEffects.playClick();
                setActiveView('assignments');
                await loadAssignments(selectedLeague.id);
                await loadAvailableAssignments(selectedLeague.id);
              }}
            >
              <FaGraduationCap /> Assignments
            </button>
            <button
              className="curriculum-btn"
              onClick={async () => {
                soundEffects.playClick();
                setActiveView('curriculum');
                await loadCurriculum(selectedLeague.id);
              }}
            >
              <FaBook /> Curriculum
            </button>
            <button
              className="challenges-btn"
              onClick={async () => {
                soundEffects.playClick();
                setActiveView('challenges');
                await loadChallenges(selectedLeague.id);
              }}
            >
              <FaTrophy /> Challenges
            </button>
          </div>

          {showCreateStudent && (
            <motion.div
              className="create-student-modal"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="modal-content">
                <h3>Create Student Account</h3>
                <form onSubmit={handleCreateStudent}>
                  <div className="form-group">
                    <label>Username</label>
                    <input
                      type="text"
                      value={newStudent.username}
                      onChange={(e) => setNewStudent({ ...newStudent, username: e.target.value })}
                      required
                      placeholder="Enter username"
                    />
                  </div>
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={newStudent.password}
                      onChange={(e) => setNewStudent({ ...newStudent, password: e.target.value })}
                      required
                      placeholder="Enter password"
                      minLength={6}
                    />
                  </div>
                  <div className="modal-actions">
                    <button type="submit" className="submit-btn">
                      <FaCheckCircle /> Create Account
                    </button>
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={() => {
                        setShowCreateStudent(false);
                        setNewStudent({ username: '', password: '' });
                      }}
                    >
                      <FaTimesCircle /> Cancel
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}

          <div className="students-list">
            <h3>Students ({selectedLeague.students?.length || 0})</h3>
            {selectedLeague.students && selectedLeague.students.length > 0 ? (
              <div className="students-table">
                <table>
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Level</th>
                      <th>XP</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedLeague.students.map((student) => (
                      <tr key={student.id}>
                        <td>{student.username || 'N/A'}</td>
                        <td>{student.level || 1}</td>
                        <td>{student.xp || 0}</td>
                        <td>{new Date(student.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button
                            className="action-btn view-btn"
                            onClick={() => handleViewStudentAnalytics(student)}
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                          >
                            <FaChartBar /> Analytics
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <FaUsers size={48} />
                <p>No students in this league yet. Create student accounts to get started.</p>
              </div>
            )}
          </div>
        </div>
      ) : activeView === 'analytics' && selectedLeague && analytics ? (
        <div className="league-analytics">
          <div className="analytics-header">
            <button
              className="back-btn"
              onClick={() => {
                soundEffects.playClick();
                setActiveView('students');
              }}
            >
              <FaArrowLeft />
            </button>
            <h2>Analytics - {selectedLeague.name}</h2>
          </div>

          <div className="analytics-grid">
            {/* Overview KPIs */}
            <div className="analytics-card">
              <h3><span className="card-icon blue"><FaUsers /></span> Overview</h3>
              <div className="stat-row">
                <div className="stat-item">
                  <span className="stat-label">Total Students</span>
                  <span className="stat-value">{analytics.overview.totalStudents}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Active (30d)</span>
                  <span className="stat-value">{analytics.overview.activeStudents}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Avg Level</span>
                  <span className="stat-value">{analytics.overview.avgLevel}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Avg XP</span>
                  <span className="stat-value">{analytics.overview.avgXp}</span>
                </div>
              </div>
            </div>

            {/* Engagement KPIs + Doughnut */}
            <div className="analytics-card">
              <h3><span className="card-icon green"><FaGraduationCap /></span> Engagement</h3>
              <div className="stat-row">
                <div className="stat-item">
                  <span className="stat-label">Lessons Done</span>
                  <span className="stat-value">{analytics.engagement.lessonsCompleted}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Quiz Attempts</span>
                  <span className="stat-value">{analytics.engagement.quizAttempts}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Avg Score</span>
                  <span className="stat-value">{analytics.engagement.avgQuizScore}%</span>
                </div>
              </div>
              {(analytics.engagement.lessonsCompleted > 0 || analytics.engagement.quizAttempts > 0) && (
                <div className="chart-container small" style={{ maxWidth: 220, margin: '20px auto 0' }}>
                  <Doughnut
                    data={{
                      labels: ['Lessons', 'Quizzes'],
                      datasets: [{
                        data: [analytics.engagement.lessonsCompleted, analytics.engagement.quizAttempts],
                        backgroundColor: ['#3b82f6', '#f59e0b'],
                        borderWidth: 0
                      }]
                    }}
                    options={{ responsive: true, maintainAspectRatio: true, plugins: { legend: { position: 'bottom', labels: { padding: 14, usePointStyle: true } } } }}
                  />
                </div>
              )}
            </div>

            {/* Portfolio KPIs */}
            <div className="analytics-card">
              <h3><span className="card-icon purple"><FaChartBar /></span> Portfolios</h3>
              <div className="stat-row">
                <div className="stat-item">
                  <span className="stat-label">Portfolios</span>
                  <span className="stat-value">{analytics.portfolio.totalPortfolios}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Avg Value</span>
                  <span className="stat-value">${analytics.portfolio.avgPortfolioValue}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Value</span>
                  <span className="stat-value">${analytics.portfolio.totalPortfolioValue}</span>
                </div>
              </div>
            </div>

            {/* Daily Activity Line Chart */}
            {analytics.dailyActivity && analytics.dailyActivity.length > 0 && (
              <div className="analytics-card full-width">
                <h3><span className="card-icon cyan"><FaCalendar /></span> Daily Activity (Last 30 Days)</h3>
                <div className="chart-container">
                  <Line
                    data={{
                      labels: analytics.dailyActivity.map(d => {
                        const dt = new Date(d.date + 'T00:00:00');
                        return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                      }),
                      datasets: [
                        {
                          label: 'Lessons',
                          data: analytics.dailyActivity.map(d => d.lessons),
                          borderColor: '#3b82f6',
                          backgroundColor: 'rgba(59,130,246,0.1)',
                          fill: true,
                          tension: 0.35,
                          pointRadius: 2,
                          pointHoverRadius: 5
                        },
                        {
                          label: 'Quizzes',
                          data: analytics.dailyActivity.map(d => d.quizzes),
                          borderColor: '#f59e0b',
                          backgroundColor: 'rgba(245,158,11,0.1)',
                          fill: true,
                          tension: 0.35,
                          pointRadius: 2,
                          pointHoverRadius: 5
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: { mode: 'index', intersect: false },
                      scales: {
                        y: { beginAtZero: true, ticks: { stepSize: 1, color: '#6b7280' }, grid: { color: '#f3f4f6' } },
                        x: { ticks: { maxRotation: 45, color: '#6b7280', maxTicksLimit: 15 }, grid: { display: false } }
                      },
                      plugins: { legend: { position: 'top', labels: { usePointStyle: true, padding: 16 } } }
                    }}
                  />
                </div>
              </div>
            )}

            {/* Quiz Score Distribution Bar */}
            {analytics.quizScoreDistribution && analytics.quizScoreDistribution.some(b => b.count > 0) && (
              <div className="analytics-card">
                <h3><span className="card-icon amber"><FaClipboardCheck /></span> Quiz Score Distribution</h3>
                <div className="chart-container">
                  <Bar
                    data={{
                      labels: analytics.quizScoreDistribution.map(b => b.bucket + '%'),
                      datasets: [{
                        label: 'Attempts',
                        data: analytics.quizScoreDistribution.map(b => b.count),
                        backgroundColor: ['#ef4444','#f97316','#f59e0b','#eab308','#84cc16','#22c55e'],
                        borderRadius: 6,
                        maxBarThickness: 48
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: { beginAtZero: true, ticks: { stepSize: 1, color: '#6b7280' }, grid: { color: '#f3f4f6' } },
                        x: { ticks: { color: '#6b7280' }, grid: { display: false } }
                      },
                      plugins: { legend: { display: false } }
                    }}
                  />
                </div>
              </div>
            )}

            {/* Level Distribution Bar */}
            {analytics.levelDistribution && analytics.levelDistribution.length > 0 && (
              <div className="analytics-card">
                <h3><span className="card-icon rose"><FaTrophy /></span> Level Distribution</h3>
                <div className="chart-container">
                  <Bar
                    data={{
                      labels: analytics.levelDistribution.map(d => 'Level ' + d.level),
                      datasets: [{
                        label: 'Students',
                        data: analytics.levelDistribution.map(d => d.count),
                        backgroundColor: 'rgba(124,58,237,0.7)',
                        borderRadius: 6,
                        maxBarThickness: 48
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: 'y',
                      scales: {
                        x: { beginAtZero: true, ticks: { stepSize: 1, color: '#6b7280' }, grid: { color: '#f3f4f6' } },
                        y: { ticks: { color: '#374151' }, grid: { display: false } }
                      },
                      plugins: { legend: { display: false } }
                    }}
                  />
                </div>
              </div>
            )}

            {/* Student XP Rankings Table */}
            {analytics.studentRankings && analytics.studentRankings.length > 0 && (
              <div className="analytics-card full-width">
                <h3><span className="card-icon amber"><FaTrophy /></span> Student Rankings (by XP)</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table className="student-ranking-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Student</th>
                        <th>Level</th>
                        <th>XP</th>
                        <th>Lessons</th>
                        <th>Avg Quiz</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.studentRankings.map((s, i) => (
                        <tr key={i}>
                          <td>
                            <span className={`rank-badge ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : 'default'}`}>
                              {i + 1}
                            </span>
                          </td>
                          <td style={{ fontWeight: 600 }}>{s.username}</td>
                          <td>{s.level}</td>
                          <td>{s.xp}</td>
                          <td>{s.lessonsCompleted}</td>
                          <td>{s.avgQuizScore}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : activeView === 'student-analytics' && selectedStudent && studentAnalytics ? (
        <div className="student-analytics">
          <div className="analytics-header">
            <button
              className="back-btn"
              onClick={() => {
                soundEffects.playClick();
                setActiveView('students');
                setSelectedStudent(null);
                setStudentAnalytics(null);
              }}
            >
              <FaArrowLeft />
            </button>
            <h2>Student Analytics - {studentAnalytics.student.username}</h2>
          </div>

          <div className="analytics-grid">
            {/* Progress Card */}
            {studentAnalytics.analytics.progress && (
              <div className="analytics-card">
                <h3><span className="card-icon blue"><FaGraduationCap /></span> Progress</h3>
                <div className="stat-row">
                  <div className="stat-item">
                    <span className="stat-label">Level</span>
                    <span className="stat-value">{studentAnalytics.analytics.progress.level}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">XP</span>
                    <span className="stat-value">{studentAnalytics.analytics.progress.xp}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Streak</span>
                    <span className="stat-value">{studentAnalytics.analytics.progress.streak}d</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Badges</span>
                    <span className="stat-value">
                      {Array.isArray(studentAnalytics.analytics.progress.badges)
                        ? studentAnalytics.analytics.progress.badges.length
                        : 0}
                    </span>
                  </div>
                </div>
                {studentAnalytics.analytics.progress.lastActiveDate && (
                  <div className="stat-item" style={{ marginTop: '12px' }}>
                    <span className="stat-label">Last Active</span>
                    <span className="stat-value" style={{ fontSize: '1rem' }}>
                      {new Date(studentAnalytics.analytics.progress.lastActiveDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Engagement Card + Bar Chart */}
            <div className="analytics-card">
              <h3><span className="card-icon green"><FaBook /></span> Engagement</h3>
              <div className="stat-row">
                <div className="stat-item">
                  <span className="stat-label">Lessons</span>
                  <span className="stat-value">{studentAnalytics.analytics.engagement.lessonsCompleted}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Facts</span>
                  <span className="stat-value">{studentAnalytics.analytics.engagement.factsCompleted}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Quizzes</span>
                  <span className="stat-value">{studentAnalytics.analytics.engagement.quizAttempts}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Avg Score</span>
                  <span className="stat-value">{studentAnalytics.analytics.engagement.avgQuizScore}%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Best Score</span>
                  <span className="stat-value">{studentAnalytics.analytics.engagement.bestQuizScore}%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Unit Tests</span>
                  <span className="stat-value">{studentAnalytics.analytics.engagement.unitTestAttempts}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">UT Avg</span>
                  <span className="stat-value">{studentAnalytics.analytics.engagement.unitTestAvgScore}%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">UT Passed</span>
                  <span className="stat-value">{studentAnalytics.analytics.engagement.unitTestsPassed}</span>
                </div>
              </div>

              {/* Engagement bar chart */}
              <div className="chart-container small" style={{ marginTop: 20 }}>
                <Bar
                  data={{
                    labels: ['Lessons', 'Facts', 'Quizzes', 'Unit Tests'],
                    datasets: [{
                      label: 'Completed',
                      data: [
                        studentAnalytics.analytics.engagement.lessonsCompleted,
                        studentAnalytics.analytics.engagement.factsCompleted,
                        studentAnalytics.analytics.engagement.quizAttempts,
                        studentAnalytics.analytics.engagement.unitTestAttempts
                      ],
                      backgroundColor: ['#3b82f6','#8b5cf6','#f59e0b','#10b981'],
                      borderRadius: 6,
                      maxBarThickness: 44
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: { beginAtZero: true, ticks: { stepSize: 1, color: '#6b7280' }, grid: { color: '#f3f4f6' } },
                      x: { ticks: { color: '#374151' }, grid: { display: false } }
                    },
                    plugins: { legend: { display: false } }
                  }}
                />
              </div>
            </div>

            {/* Portfolio Card */}
            <div className="analytics-card">
              <h3><span className="card-icon purple"><FaChartBar /></span> Portfolio</h3>
              <div className="stat-row">
                <div className="stat-item">
                  <span className="stat-label">Portfolios</span>
                  <span className="stat-value">{studentAnalytics.analytics.portfolio.portfolioCount}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Avg Value</span>
                  <span className="stat-value">${studentAnalytics.analytics.portfolio.avgPortfolioValue}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Value</span>
                  <span className="stat-value">${studentAnalytics.analytics.portfolio.totalPortfolioValue}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Max Value</span>
                  <span className="stat-value">${studentAnalytics.analytics.portfolio.maxPortfolioValue}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Transactions</span>
                  <span className="stat-value">{studentAnalytics.analytics.portfolio.transactionsCount}</span>
                </div>
              </div>
            </div>

            {/* Activity Timeline as Line Chart + Detail List */}
            {studentAnalytics.analytics.activity && studentAnalytics.analytics.activity.length > 0 && (
              <>
                <div className="analytics-card full-width">
                  <h3><span className="card-icon cyan"><FaCalendar /></span> Activity Chart (Last 30 Days)</h3>
                  <div className="chart-container">
                    <Bar
                      data={{
                        labels: studentAnalytics.analytics.activity.map(d => {
                          const dt = new Date(d.date + 'T00:00:00');
                          return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                        }),
                        datasets: [{
                          label: 'Activities',
                          data: studentAnalytics.analytics.activity.map(d => d.count),
                          backgroundColor: 'rgba(99,102,241,0.65)',
                          borderRadius: 4,
                          maxBarThickness: 28
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: { beginAtZero: true, ticks: { stepSize: 1, color: '#6b7280' }, grid: { color: '#f3f4f6' } },
                          x: { ticks: { color: '#6b7280' }, grid: { display: false } }
                        },
                        plugins: { legend: { display: false } }
                      }}
                    />
                  </div>
                </div>

                <div className="analytics-card full-width">
                  <h3><span className="card-icon green"><FaBook /></span> Activity Details</h3>
                  <div className="activity-timeline">
                    {studentAnalytics.analytics.activity.map((dayActivity, index) => (
                      <div key={index} className="activity-day">
                        <div className="activity-day-header">
                          <span className="activity-date">
                            {new Date(dayActivity.date + 'T00:00:00').toLocaleDateString()}
                          </span>
                          <span className="activity-count">{dayActivity.count} {dayActivity.count === 1 ? 'activity' : 'activities'}</span>
                        </div>
                        {dayActivity.activities && dayActivity.activities.length > 0 && (
                          <div className="activity-list">
                            {dayActivity.activities.map((activity, actIndex) => (
                              <div key={actIndex} className="activity-detail">
                                <span className={`activity-type activity-type-${activity.type}`}>
                                  {activity.type === 'lesson' && 'Lesson'}
                                  {activity.type === 'quiz' && 'Quiz'}
                                  {activity.type === 'fact' && 'Fact'}
                                </span>
                                <span className="activity-name">{activity.name}</span>
                                {activity.type === 'quiz' && activity.score !== null && (
                                  <span className={`activity-score ${activity.passed ? 'passed' : 'failed'}`}>
                                    {activity.score}% {activity.passed ? '✓' : '✗'}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ) : activeView === 'assignments' && selectedLeague ? (
        <div className="assignments-view">
          <div className="analytics-header">
            <button
              className="back-btn"
              onClick={() => {
                soundEffects.playClick();
                setActiveView('students');
              }}
            >
              <FaArrowLeft />
            </button>
            <h2>Assignments - {selectedLeague.name}</h2>
          </div>

          <div className="assignments-actions">
            <button
              className="assign-btn"
              onClick={async () => {
                soundEffects.playClick();
                setAssignType('lesson');
                setShowAssignModal(true);
                await loadAvailableAssignments(selectedLeague.id);
              }}
            >
              <FaGraduationCap /> Assign Lesson
            </button>
            <button
              className="assign-btn"
              onClick={async () => {
                soundEffects.playClick();
                setAssignType('quiz');
                setShowAssignModal(true);
                await loadAvailableAssignments(selectedLeague.id);
              }}
            >
              <FaClipboardCheck /> Assign Quiz
            </button>
          </div>

          {loadingAssignments ? (
            <div className="loading-state">
              <p>Loading assignments...</p>
            </div>
          ) : (
            <div className="assignments-content">
              <div className="assignments-section">
                <h3>Assigned Lessons ({assignments.lessons.length})</h3>
                {assignments.lessons.length > 0 ? (
                  <div className="assignments-list">
                    {assignments.lessons.map((lesson) => (
                      <div key={lesson.lessonId} className="assignment-item">
                        <div className="assignment-info">
                          <h4>{lesson.title}</h4>
                          <p>{lesson.courseTitle} • {lesson.unitTitle}</p>
                          <small>Assigned: {new Date(lesson.assignedAt).toLocaleDateString()}</small>
                        </div>
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveAssignment(lesson.lessonId, null)}
                        >
                          <FaTrash /> Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty-message">No lessons assigned yet</p>
                )}
              </div>

              <div className="assignments-section">
                <h3>Assigned Quizzes ({assignments.quizzes.length})</h3>
                {assignments.quizzes.length > 0 ? (
                  <div className="assignments-list">
                    {assignments.quizzes.map((quiz) => (
                      <div key={quiz.quizId} className="assignment-item">
                        <div className="assignment-info">
                          <h4>{quiz.title}</h4>
                          <p>{quiz.courseTitle} • {quiz.unitTitle}</p>
                          <small>Assigned: {new Date(quiz.assignedAt).toLocaleDateString()}</small>
                        </div>
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveAssignment(null, quiz.quizId)}
                        >
                          <FaTrash /> Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty-message">No quizzes assigned yet</p>
                )}
              </div>
            </div>
          )}

          {showAssignModal && (
            <motion.div
              className="assign-modal"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="modal-content">
                <h3>Assign {assignType === 'lesson' ? 'Lesson' : 'Quiz'}</h3>
                <div className="form-group">
                  <label>Select Course, Unit, or {assignType === 'lesson' ? 'Lesson' : 'Quiz'}</label>
                  <div className="hierarchy-dropdown-container">
                    <select
                      value={selectedAssignId || ''}
                      onChange={(e) => setSelectedAssignId(e.target.value)}
                      className="hierarchy-dropdown"
                    >
                      <option value="">Choose a {assignType}...</option>
                      {organizeAssignments(
                        assignType === 'lesson' ? availableAssignments.lessons : availableAssignments.quizzes,
                        assignType === 'quiz'
                      ).map((course) => (
                        <React.Fragment key={course.id}>
                          <option value={`course-${course.id}`} className="course-option">
                            📚 {course.title} {course.units?.[0]?.items?.[0]?.source === 'teacher' && '(Teacher)'} (All {assignType}s)
                          </option>
                          {course.units.map((unit) => (
                            <React.Fragment key={unit.id}>
                              <option value={`unit-${course.id}-${unit.id}`} className="unit-option">
                                &nbsp;&nbsp;📖 {unit.title} (All {assignType}s)
                              </option>
                              {unit.items.map((item) => {
                                if (item.type === 'lesson' && assignType === 'lesson') {
                                  return (
                                    <option
                                      key={item.id}
                                      value={item.id}
                                      className="lesson-option"
                                    >
                                      &nbsp;&nbsp;&nbsp;&nbsp;📄 {item.title} {item.source === 'teacher' && '(Teacher)'}
                                    </option>
                                  );
                                } else if (item.type === 'lesson' && assignType === 'quiz') {
                                  // For quizzes, show lesson as a group header
                                  return (
                                    <React.Fragment key={`lesson-${item.id}`}>
                                      <option value={`lesson-${item.id}`} className="lesson-option" disabled>
                                        &nbsp;&nbsp;&nbsp;&nbsp;📄 {item.title} {item.source === 'teacher' && '(Teacher)'}
                                      </option>
                                      {item.quizzes && item.quizzes.map((quiz) => (
                                        <option
                                          key={quiz.id}
                                          value={quiz.id}
                                          className="quiz-option"
                                        >
                                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;📝 {quiz.title} {quiz.source === 'teacher' && '(Teacher)'}
                                        </option>
                                      ))}
                                    </React.Fragment>
                                  );
                                } else if (item.type === 'quiz') {
                                  return (
                                    <option
                                      key={item.id}
                                      value={item.id}
                                      className="quiz-option"
                                    >
                                      &nbsp;&nbsp;&nbsp;&nbsp;📝 {item.title} {item.source === 'teacher' && '(Teacher)'}
                                    </option>
                                  );
                                }
                                return null;
                              })}
                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={handleAssign}
                    disabled={!selectedAssignId}
                  >
                    <FaCheckCircle /> Assign
                  </button>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => {
                      setShowAssignModal(false);
                      setSelectedAssignId(null);
                    }}
                  >
                    <FaTimesCircle /> Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      ) : activeView === 'curriculum' && selectedLeague ? (
        <div className="curriculum-view">
          <div className="analytics-header">
            <button
              className="back-btn"
              onClick={() => {
                soundEffects.playClick();
                setActiveView('students');
              }}
            >
              <FaArrowLeft />
            </button>
            <h2>Curriculum - {selectedLeague.name}</h2>
          </div>

          <div className="curriculum-actions">
            <button
              className="create-btn"
              onClick={() => {
                soundEffects.playClick();
                setShowCreateLesson(true);
              }}
            >
              <FaPlus /> Create Lesson
            </button>
            <button
              className="create-btn create-btn-pdf"
              onClick={() => {
                soundEffects.playClick();
                setShowPdfUploader(true);
              }}
            >
              <FaFilePdf /> Create from PDF
            </button>
            <button
              className="create-btn"
              onClick={() => {
                soundEffects.playClick();
                setShowCreateQuiz(true);
              }}
            >
              <FaPlus /> Create Quiz
            </button>
          </div>

          {loadingCurriculum ? (
            <div className="loading-state">
              <p>Loading curriculum...</p>
            </div>
          ) : (
            <div className="curriculum-content">
              <div className="curriculum-section">
                <h3>Lessons ({curriculumLessons.length})</h3>
                {curriculumLessons.length > 0 ? (
                  <div className="curriculum-list">
                    {curriculumLessons.map((lesson) => {
                      // Check if this lesson is already assigned
                      // For teacher lessons, the backend returns teacherLessonId as "lessonId" in the assignments array
                      const isAssigned = assignments.lessons.some(
                        assigned => {
                          // Check lessonId (which contains teacher lesson ID for teacher lessons)
                          // Convert both to strings for comparison to handle type mismatches
                          const assignedId = String(assigned.lessonId || assigned.teacherLessonId || '');
                          const lessonId = String(lesson.id || '');
                          return assignedId === lessonId && assignedId !== '';
                        }
                      );
                      
                      return (
                        <div key={lesson.id} className="curriculum-item">
                          {editingLesson === lesson.id ? (
                            <div className="edit-form">
                              <div className="form-group">
                                <label>Title</label>
                                <input
                                  type="text"
                                  value={lessonForm.title}
                                  onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                                />
                              </div>
                              <div className="form-group">
                                <label>Content</label>
                                <RichTextEditor
                                  value={lessonForm.content}
                                  onChange={(content) => setLessonForm({ ...lessonForm, content })}
                                />
                              </div>
                              <div className="form-group">
                                <label>Order</label>
                                <input
                                  type="number"
                                  value={lessonForm.order}
                                  onChange={(e) => setLessonForm({ ...lessonForm, order: parseInt(e.target.value) || 0 })}
                                />
                              </div>
                              <div className="form-group">
                                <label>XP Value</label>
                                <input
                                  type="number"
                                  value={lessonForm.xpValue}
                                  onChange={(e) => setLessonForm({ ...lessonForm, xpValue: parseInt(e.target.value) || 50 })}
                                />
                              </div>
                              <div className="form-group">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={lessonForm.isActive}
                                    onChange={(e) => setLessonForm({ ...lessonForm, isActive: e.target.checked })}
                                  />
                                  Active
                                </label>
                              </div>
                              <div className="form-actions">
                                <button onClick={() => handleUpdateLesson(lesson.id)} className="save-btn">
                                  <FaSave /> Save
                                </button>
                                <button onClick={() => {
                                  setEditingLesson(null);
                                  setLessonForm({ title: '', content: '', order: 0, xpValue: 50, isActive: true });
                                }} className="cancel-btn">
                                  <FaTimesCircle /> Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="curriculum-info">
                                <h4>{lesson.title}</h4>
                                <p>Order: {lesson.order} | XP: {lesson.xpValue} | Completions: {lesson.completion_count || 0}</p>
                                <small>Created: {new Date(lesson.createdAt).toLocaleDateString()}</small>
                              </div>
                              <div className="curriculum-actions-item">
                                {isAssigned ? (
                                  <button onClick={async () => {
                                    soundEffects.playClick();
                                    try {
                                      await LeagueService.removeAssignment(selectedLeague.id, { lessonId: lesson.id });
                                      toast.success('Lesson unassigned successfully');
                                      await loadAssignments(selectedLeague.id);
                                    } catch (error) {
                                      toast.error(error.error || 'Failed to unassign lesson');
                                    }
                                  }} className="remove-btn-small" title="Unassign from League">
                                    <FaTimesCircle /> Unassign
                                  </button>
                                ) : (
                                  <button onClick={async () => {
                                    soundEffects.playClick();
                                    try {
                                      await LeagueService.assignToLeague(selectedLeague.id, { lessonId: lesson.id });
                                      toast.success('Lesson assigned successfully');
                                      await loadAssignments(selectedLeague.id);
                                    } catch (error) {
                                      toast.error(error.error || 'Failed to assign lesson');
                                    }
                                  }} className="assign-btn-small" title="Assign to League">
                                    <FaGraduationCap /> Assign
                                  </button>
                                )}
                                <button onClick={() => {
                                  setEditingLesson(lesson.id);
                                  setLessonForm({ title: lesson.title, content: lesson.content, order: lesson.order, xpValue: lesson.xpValue, isActive: lesson.isActive });
                                }} className="edit-btn">
                                  <FaEdit /> Edit
                                </button>
                                <button onClick={() => handleDeleteLesson(lesson.id)} className="delete-btn">
                                  <FaTrash /> Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="empty-message">No lessons created yet</p>
                )}
              </div>

              <div className="curriculum-section">
                <h3>Quizzes ({curriculumQuizzes.length})</h3>
                {curriculumQuizzes.length > 0 ? (
                  <div className="curriculum-list">
                    {curriculumQuizzes.map((quiz) => {
                      // Check if this quiz is already assigned
                      // For teacher quizzes, the backend returns teacherQuizId as "quizId" in the assignments array
                      const isAssigned = assignments.quizzes.some(
                        assigned => {
                          // Check quizId (which contains teacher quiz ID for teacher quizzes)
                          // Convert both to strings for comparison to handle type mismatches
                          const assignedId = String(assigned.quizId || assigned.teacherQuizId || '');
                          const quizId = String(quiz.id || '');
                          return assignedId === quizId && assignedId !== '';
                        }
                      );
                      
                      return (
                        <div key={quiz.id} className="curriculum-item">
                          {editingQuiz === quiz.id ? (
                            <div className="edit-form">
                              <div className="form-group">
                                <label>Title</label>
                                <input
                                  type="text"
                                  value={quizForm.title}
                                  onChange={(e) => setQuizForm({ ...quizForm, title: e.target.value })}
                                />
                              </div>
                              <div className="form-group">
                                <label>Description</label>
                                <textarea
                                  value={quizForm.description}
                                  onChange={(e) => setQuizForm({ ...quizForm, description: e.target.value })}
                                  rows="3"
                                />
                              </div>
                              <div className="form-group">
                                <label>Passing Score (%)</label>
                                <input
                                  type="number"
                                  value={quizForm.passingScore}
                                  onChange={(e) => setQuizForm({ ...quizForm, passingScore: parseInt(e.target.value) || 70 })}
                                  min="0"
                                  max="100"
                                />
                              </div>
                              <div className="form-group">
                                <label>XP Value</label>
                                <input
                                  type="number"
                                  value={quizForm.xpValue}
                                  onChange={(e) => setQuizForm({ ...quizForm, xpValue: parseInt(e.target.value) || 25 })}
                                  min="0"
                                />
                              </div>
                              <div className="form-group">
                                <label>Time Limit (minutes, optional)</label>
                                <input
                                  type="number"
                                  value={quizForm.timeLimit || ''}
                                  onChange={(e) => setQuizForm({ ...quizForm, timeLimit: e.target.value ? parseInt(e.target.value) : null })}
                                  min="0"
                                  placeholder="No time limit"
                                />
                              </div>
                              <div className="form-group">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={quizForm.isActive}
                                    onChange={(e) => setQuizForm({ ...quizForm, isActive: e.target.checked })}
                                  />
                                  Active
                                </label>
                              </div>
                              <div className="form-actions">
                                <button onClick={() => handleUpdateQuiz(quiz.id)} className="save-btn">
                                  <FaSave /> Save
                                </button>
                                <button onClick={() => {
                                  setEditingQuiz(null);
                                  setQuizForm({ lessonId: '', title: '', description: '', type: 'multiple_choice', questions: [], timeLimit: '', passingScore: 70, xpValue: 25, isActive: true });
                                }} className="cancel-btn">
                                  <FaTimesCircle /> Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="curriculum-info">
                                <h4>{quiz.title}</h4>
                                <p>{quiz.description || 'No description'}</p>
                                <p>Type: {quiz.type} | Passing Score: {quiz.passingScore}% | XP: {quiz.xpValue} | Attempts: {quiz.attempt_count || 0}</p>
                                <p className="questions-count"><FaQuestion /> Questions: {Array.isArray(quiz.questions) ? quiz.questions.length : 0}</p>
                                {quiz.timeLimit && <p>Time Limit: {quiz.timeLimit} minutes</p>}
                                <small>Created: {new Date(quiz.createdAt).toLocaleDateString()}</small>
                              </div>
                              <div className="curriculum-actions-item">
                                {isAssigned ? (
                                  <button onClick={async () => {
                                    soundEffects.playClick();
                                    try {
                                      await LeagueService.removeAssignment(selectedLeague.id, { quizId: quiz.id });
                                      toast.success('Quiz unassigned successfully');
                                      await loadAssignments(selectedLeague.id);
                                    } catch (error) {
                                      toast.error(error.error || 'Failed to unassign quiz');
                                    }
                                  }} className="remove-btn-small" title="Unassign from League">
                                    <FaTimesCircle /> Unassign
                                  </button>
                                ) : (
                                  <button onClick={async () => {
                                    soundEffects.playClick();
                                    try {
                                      await LeagueService.assignToLeague(selectedLeague.id, { quizId: quiz.id });
                                      toast.success('Quiz assigned successfully');
                                      await loadAssignments(selectedLeague.id);
                                    } catch (error) {
                                      toast.error(error.error || 'Failed to assign quiz');
                                    }
                                  }} className="assign-btn-small" title="Assign to League">
                                    <FaGraduationCap /> Assign
                                  </button>
                                )}
                                <button onClick={() => handleStartEditQuestions(quiz)} className="edit-questions-btn" title="Edit Quiz Questions">
                                  <FaClipboardCheck /> Edit Questions
                                </button>
                                <button onClick={() => {
                                  setEditingQuiz(quiz.id);
                                  setQuizForm({
                                    lessonId: quiz.lessonId || '',
                                    title: quiz.title || '',
                                    description: quiz.description || '',
                                    type: quiz.type || 'multiple_choice',
                                    questions: Array.isArray(quiz.questions) ? quiz.questions : (typeof quiz.questions === 'string' ? JSON.parse(quiz.questions || '[]') : []),
                                    timeLimit: quiz.timeLimit || '',
                                    passingScore: quiz.passingScore || 70,
                                    xpValue: quiz.xpValue || 25,
                                    isActive: quiz.isActive !== undefined ? quiz.isActive : true
                                  });
                                }} className="edit-btn">
                                  <FaEdit /> Settings
                                </button>
                                <button onClick={() => handleDeleteQuiz(quiz.id)} className="delete-btn">
                                  <FaTrash /> Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="empty-message">No quizzes created yet</p>
                )}
              </div>
            </div>
          )}

          {/* Quiz Questions Editor Modal */}
          {editingQuizQuestions && (
            <motion.div 
              className="quiz-questions-editor-overlay" 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }}
              onClick={handleCancelEditQuestions}
            >
              <motion.div 
                className="quiz-questions-editor-modal"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="quiz-editor-header">
                  <h2>
                    <FaClipboardCheck /> Edit Questions: {quizForm.title}
                  </h2>
                  <div className="editor-actions">
                    <button className="save-btn" onClick={handleSaveQuizQuestions}>
                      <FaSave /> Save Questions
                    </button>
                    <button className="cancel-btn" onClick={handleCancelEditQuestions}>
                      <FaTimes /> Cancel
                    </button>
                  </div>
                </div>

                <div className="questions-editor-content">
                  {editableQuestions.length === 0 ? (
                    <div className="no-questions">
                      <p>No questions yet. Click "Add Question" to get started.</p>
                    </div>
                  ) : (
                    editableQuestions.map((question, index) => (
                      <div key={question.id || index} className="question-editor-card">
                        <div className="question-card-header">
                          <div className="question-number">
                            <FaGripVertical className="grip-icon" />
                            Question {index + 1}
                          </div>
                          <div className="question-controls">
                            <button
                              className="btn-move"
                              onClick={() => handleMoveQuizQuestion(index, 'up')}
                              disabled={index === 0}
                              title="Move Up"
                            >
                              <FaArrowUp />
                            </button>
                            <button
                              className="btn-move"
                              onClick={() => handleMoveQuizQuestion(index, 'down')}
                              disabled={index === editableQuestions.length - 1}
                              title="Move Down"
                            >
                              <FaArrowDown />
                            </button>
                            <button
                              className="btn-delete-question"
                              onClick={() => handleDeleteQuizQuestion(index)}
                              title="Delete Question"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>

                        <div className="question-card-body">
                          <div className="form-group">
                            <label>Question Type</label>
                            <select
                              value={question.type || 'multiple_choice'}
                              onChange={(e) => handleQuizQuestionChange(index, 'type', e.target.value)}
                            >
                              <option value="multiple_choice">Multiple Choice</option>
                              <option value="true_false">True/False</option>
                              <option value="short_answer">Short Answer</option>
                            </select>
                          </div>

                          <div className="form-group">
                            <label>Question Text</label>
                            <textarea
                              value={question.question || ''}
                              onChange={(e) => handleQuizQuestionChange(index, 'question', e.target.value)}
                              rows="3"
                              placeholder="Enter the question text..."
                            />
                          </div>

                          {question.type === 'multiple_choice' && (
                            <div className="form-group options-group">
                              <label>Options (select the correct answer)</label>
                              {question.options?.map((option, optIndex) => (
                                <div key={optIndex} className={`option-row ${question.correctAnswer === optIndex ? 'correct-option' : ''}`}>
                                  <span className="option-letter">
                                    {String.fromCharCode(65 + optIndex)}.
                                  </span>
                                  <input
                                    type="text"
                                    value={option || ''}
                                    onChange={(e) => handleQuizOptionChange(index, optIndex, e.target.value)}
                                    placeholder={`Option ${optIndex + 1}`}
                                    className="option-input"
                                  />
                                  <input
                                    type="radio"
                                    name={`correct-${index}`}
                                    checked={question.correctAnswer === optIndex}
                                    onChange={() => handleQuizQuestionChange(index, 'correctAnswer', optIndex)}
                                    className="correct-radio"
                                    title="Mark as correct answer"
                                  />
                                  {question.options.length > 2 && (
                                    <button
                                      className="btn-remove-option"
                                      onClick={() => handleRemoveQuizOption(index, optIndex)}
                                      title="Remove Option"
                                    >
                                      <FaTimes />
                                    </button>
                                  )}
                                </div>
                              ))}
                              <button
                                className="btn-add-option"
                                onClick={() => handleAddQuizOption(index)}
                              >
                                <FaPlus /> Add Option
                              </button>
                            </div>
                          )}

                          {question.type === 'true_false' && (
                            <div className="form-group">
                              <label>Correct Answer</label>
                              <div className="true-false-options">
                                <label className={question.correctAnswer === true ? 'selected-answer' : ''}>
                                  <input
                                    type="radio"
                                    name={`tf-${index}`}
                                    checked={question.correctAnswer === true}
                                    onChange={() => handleQuizQuestionChange(index, 'correctAnswer', true)}
                                  />
                                  True
                                </label>
                                <label className={question.correctAnswer === false ? 'selected-answer' : ''}>
                                  <input
                                    type="radio"
                                    name={`tf-${index}`}
                                    checked={question.correctAnswer === false}
                                    onChange={() => handleQuizQuestionChange(index, 'correctAnswer', false)}
                                  />
                                  False
                                </label>
                              </div>
                            </div>
                          )}

                          {question.type === 'short_answer' && (
                            <div className="form-group">
                              <label>Reference Answer</label>
                              <textarea
                                value={question.referenceAnswer || question.correctAnswer || ''}
                                onChange={(e) => handleQuizQuestionChange(index, 'referenceAnswer', e.target.value)}
                                rows="3"
                                placeholder="Enter the reference answer for grading..."
                              />
                              <small className="help-text">
                                This answer will be used as a reference when grading student responses.
                              </small>
                            </div>
                          )}

                          <div className="form-group">
                            <label>Explanation (Optional)</label>
                            <textarea
                              value={question.explanation || ''}
                              onChange={(e) => handleQuizQuestionChange(index, 'explanation', e.target.value)}
                              rows="2"
                              placeholder="Enter explanation shown after answering..."
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  )}

                  <button className="btn-add-question" onClick={handleAddQuizQuestion}>
                    <FaPlus /> Add Question
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {showCreateLesson && (
            <motion.div className="assign-modal" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="modal-content">
                <h3>Create Lesson</h3>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={lessonForm.title}
                    onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Content</label>
                  <RichTextEditor
                    value={lessonForm.content}
                    onChange={(content) => setLessonForm({ ...lessonForm, content })}
                  />
                </div>
                <div className="form-group">
                  <label>Order</label>
                  <input
                    type="number"
                    value={lessonForm.order}
                    onChange={(e) => setLessonForm({ ...lessonForm, order: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>XP Value</label>
                  <input
                    type="number"
                    value={lessonForm.xpValue}
                    onChange={(e) => setLessonForm({ ...lessonForm, xpValue: parseInt(e.target.value) || 50 })}
                  />
                </div>
                <div className="modal-actions">
                  <button onClick={handleCreateLesson} className="submit-btn" disabled={!lessonForm.title || !lessonForm.content}>
                    <FaCheckCircle /> Create
                  </button>
                  <button onClick={() => {
                    setShowCreateLesson(false);
                    setLessonForm({ title: '', content: '', order: 0, xpValue: 50, isActive: true });
                  }} className="cancel-btn">
                    <FaTimesCircle /> Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {showCreateQuiz && (
            <motion.div className="assign-modal" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="modal-content">
                <h3>Create Quiz</h3>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={quizForm.title}
                    onChange={(e) => setQuizForm({ ...quizForm, title: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={quizForm.description}
                    onChange={(e) => setQuizForm({ ...quizForm, description: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Passing Score (%)</label>
                  <input
                    type="number"
                    value={quizForm.passingScore}
                    onChange={(e) => setQuizForm({ ...quizForm, passingScore: parseInt(e.target.value) || 70 })}
                  />
                </div>
                <div className="form-group">
                  <label>XP Value</label>
                  <input
                    type="number"
                    value={quizForm.xpValue}
                    onChange={(e) => setQuizForm({ ...quizForm, xpValue: parseInt(e.target.value) || 25 })}
                  />
                </div>
                <div className="modal-actions">
                  <button onClick={handleCreateQuiz} className="submit-btn" disabled={!quizForm.title}>
                    <FaCheckCircle /> Create
                  </button>
                  <button onClick={() => {
                    setShowCreateQuiz(false);
                    setQuizForm({ lessonId: '', title: '', description: '', type: 'multiple_choice', questions: [], timeLimit: '', passingScore: 70, xpValue: 25, isActive: true });
                  }} className="cancel-btn">
                    <FaTimesCircle /> Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* PDF Uploader Modal for Teacher Curriculum */}
          {showPdfUploader && selectedLeague && (
            <motion.div className="assign-modal" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="modal-content modal-content-large">
                <PdfLessonUploader
                  leagueId={selectedLeague.id}
                  isTeacherCurriculum={true}
                  onLessonCreated={async (lesson) => {
                    toast.success('Lesson created from PDF successfully!');
                    setShowPdfUploader(false);
                    await loadCurriculum(selectedLeague.id);
                  }}
                  onClose={() => {
                    setShowPdfUploader(false);
                  }}
                />
              </div>
            </motion.div>
          )}
        </div>
      ) : activeView === 'challenges' && selectedLeague ? (
        <div className="challenges-view">
          <div className="analytics-header">
            <button
              className="back-btn"
              onClick={() => {
                soundEffects.playClick();
                setActiveView('students');
              }}
            >
              <FaArrowLeft />
            </button>
            <h2>Challenges - {selectedLeague.name}</h2>
          </div>

          <div className="challenges-actions">
            <button
              className="create-btn"
              onClick={() => {
                soundEffects.playClick();
                setShowCreateChallenge(true);
              }}
            >
              <FaPlus /> Create Challenge
            </button>
          </div>

          {loadingChallenges ? (
            <div className="loading-state">
              <p>Loading challenges...</p>
            </div>
          ) : (
            <div className="challenges-content">
              {challenges.length > 0 ? (
                <div className="challenges-list">
                  {challenges.map((challenge) => (
                    <div key={challenge.id} className="challenge-item">
                      {editingChallenge === challenge.id ? (
                        <div className="edit-form">
                          <div className="form-group">
                            <label>Title</label>
                            <input
                              type="text"
                              value={challengeForm.title}
                              onChange={(e) => setChallengeForm({ ...challengeForm, title: e.target.value })}
                            />
                          </div>
                          <div className="form-group">
                            <label>Description</label>
                            <textarea
                              value={challengeForm.description}
                              onChange={(e) => setChallengeForm({ ...challengeForm, description: e.target.value })}
                            />
                          </div>
                          <div className="form-group">
                            <label>Start Date</label>
                            <input
                              type="datetime-local"
                              value={challengeForm.startDate}
                              onChange={(e) => setChallengeForm({ ...challengeForm, startDate: e.target.value })}
                            />
                          </div>
                          <div className="form-group">
                            <label>End Date</label>
                            <input
                              type="datetime-local"
                              value={challengeForm.endDate}
                              onChange={(e) => setChallengeForm({ ...challengeForm, endDate: e.target.value })}
                            />
                          </div>
                          <div className="form-group">
                            <label>Starting Balance</label>
                            <input
                              type="number"
                              value={challengeForm.startingBalance}
                              onChange={(e) => setChallengeForm({ ...challengeForm, startingBalance: parseFloat(e.target.value) || 10000 })}
                            />
                          </div>
                          <div className="form-group">
                            <label>Max Participants (optional)</label>
                            <input
                              type="number"
                              value={challengeForm.maxParticipants}
                              onChange={(e) => setChallengeForm({ ...challengeForm, maxParticipants: e.target.value ? parseInt(e.target.value) : '' })}
                            />
                          </div>
                          <div className="form-group">
                            <label>Prize Pool (optional)</label>
                            <input
                              type="number"
                              value={challengeForm.prizePool}
                              onChange={(e) => setChallengeForm({ ...challengeForm, prizePool: e.target.value ? parseFloat(e.target.value) : '' })}
                            />
                          </div>
                          <div className="form-group">
                            <label>
                              <input
                                type="checkbox"
                                checked={challengeForm.isActive}
                                onChange={(e) => setChallengeForm({ ...challengeForm, isActive: e.target.checked })}
                              />
                              Active
                            </label>
                          </div>
                          <div className="form-group">
                            <label>
                              <input
                                type="checkbox"
                                checked={challengeForm.isPublished}
                                onChange={(e) => setChallengeForm({ ...challengeForm, isPublished: e.target.checked })}
                              />
                              Published
                            </label>
                          </div>
                          <div className="form-actions">
                            <button onClick={() => handleUpdateChallenge(challenge.id)} className="save-btn">
                              <FaSave /> Save
                            </button>
                            <button onClick={() => {
                              setEditingChallenge(null);
                              setChallengeForm({ title: '', description: '', startDate: '', endDate: '', startingBalance: 10000, maxParticipants: '', prizePool: '', rules: null, isActive: false, isPublished: false });
                            }} className="cancel-btn">
                              <FaTimesCircle /> Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="challenge-info">
                            <h4>{challenge.title}</h4>
                            <p>{challenge.description || 'No description'}</p>
                            <div className="challenge-details">
                              <p><FaCalendar /> {new Date(challenge.startDate).toLocaleString()} - {new Date(challenge.endDate).toLocaleString()}</p>
                              <p>Starting Balance: ${parseFloat(challenge.startingBalance).toLocaleString()}</p>
                              {challenge.maxParticipants && <p>Max Participants: {challenge.maxParticipants}</p>}
                              {challenge.prizePool && <p>Prize Pool: ${parseFloat(challenge.prizePool).toLocaleString()}</p>}
                              <p>Participants: {challenge.participant_count || 0}</p>
                              <p>Status: {challenge.isActive ? 'Active' : 'Inactive'} | {challenge.isPublished ? 'Published' : 'Draft'}</p>
                            </div>
                          </div>
                          <div className="challenge-actions-item">
                            <button onClick={() => {
                              setEditingChallenge(challenge.id);
                              setChallengeForm({
                                title: challenge.title,
                                description: challenge.description || '',
                                startDate: new Date(challenge.startDate).toISOString().slice(0, 16),
                                endDate: new Date(challenge.endDate).toISOString().slice(0, 16),
                                startingBalance: parseFloat(challenge.startingBalance),
                                maxParticipants: challenge.maxParticipants || '',
                                prizePool: challenge.prizePool ? parseFloat(challenge.prizePool) : '',
                                rules: challenge.rules,
                                isActive: challenge.isActive,
                                isPublished: challenge.isPublished
                              });
                            }} className="edit-btn">
                              <FaEdit /> Edit
                            </button>
                            <button onClick={() => handleDeleteChallenge(challenge.id)} className="delete-btn">
                              <FaTrash /> Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-message">No challenges created yet</p>
              )}
            </div>
          )}

          {showCreateChallenge && (
            <motion.div className="assign-modal" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="modal-content">
                <h3>Create Challenge</h3>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={challengeForm.title}
                    onChange={(e) => setChallengeForm({ ...challengeForm, title: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={challengeForm.description}
                    onChange={(e) => setChallengeForm({ ...challengeForm, description: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="datetime-local"
                    value={challengeForm.startDate}
                    onChange={(e) => setChallengeForm({ ...challengeForm, startDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="datetime-local"
                    value={challengeForm.endDate}
                    onChange={(e) => setChallengeForm({ ...challengeForm, endDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Starting Balance</label>
                  <input
                    type="number"
                    value={challengeForm.startingBalance}
                    onChange={(e) => setChallengeForm({ ...challengeForm, startingBalance: parseFloat(e.target.value) || 10000 })}
                  />
                </div>
                <div className="form-group">
                  <label>Max Participants (optional)</label>
                  <input
                    type="number"
                    value={challengeForm.maxParticipants}
                    onChange={(e) => setChallengeForm({ ...challengeForm, maxParticipants: e.target.value ? parseInt(e.target.value) : '' })}
                  />
                </div>
                <div className="form-group">
                  <label>Prize Pool (optional)</label>
                  <input
                    type="number"
                    value={challengeForm.prizePool}
                    onChange={(e) => setChallengeForm({ ...challengeForm, prizePool: e.target.value ? parseFloat(e.target.value) : '' })}
                  />
                </div>
                <div className="modal-actions">
                  <button onClick={handleCreateChallenge} className="submit-btn" disabled={!challengeForm.title || !challengeForm.startDate || !challengeForm.endDate}>
                    <FaCheckCircle /> Create
                  </button>
                  <button onClick={() => {
                    setShowCreateChallenge(false);
                    setChallengeForm({ title: '', description: '', startDate: '', endDate: '', startingBalance: 10000, maxParticipants: '', prizePool: '', rules: null, isActive: false, isPublished: false });
                  }} className="cancel-btn">
                    <FaTimesCircle /> Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      ) : activeView === 'teachers-list' ? (
        <div className="league-analytics">
          <div className="analytics-header">
            <button
              className="back-btn"
              onClick={() => {
                soundEffects.playClick();
                setActiveView('leagues');
              }}
            >
              <FaArrowLeft />
            </button>
            <h2>Select a Teacher</h2>
          </div>
          {teachersList.length === 0 ? (
            <div className="empty-state">
              <FaGraduationCap size={48} />
              <p>No teachers found.</p>
            </div>
          ) : (
            <div className="leagues-grid">
              {teachersList.map((t) => (
                <motion.div
                  key={t.id}
                  className="league-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => handleViewSpecificTeacherKpis(t)}
                >
                  <div className="league-card-header">
                    <h3>{t.name || t.email}</h3>
                    <span className="league-code">
                      <FaUsers /> {t.league_count || 0}
                    </span>
                  </div>
                  <p className="league-description">{t.email}</p>
                  <div className="league-actions">
                    <button
                      className="action-btn analytics-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewSpecificTeacherKpis(t);
                      }}
                    >
                      <FaTachometerAlt /> View KPIs
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      ) : activeView === 'teacher-kpis' ? (
        <div className="league-analytics">
          <div className="analytics-header">
            <button
              className="back-btn"
              onClick={() => {
                soundEffects.playClick();
                setActiveView(isAdmin ? 'teachers-list' : 'leagues');
                setTeacherKpis(null);
                setSelectedTeacher(null);
              }}
            >
              <FaArrowLeft />
            </button>
            <h2>
              Teacher KPIs{selectedTeacher ? ` — ${selectedTeacher.name || selectedTeacher.email}` : ''}
            </h2>
          </div>

          {loadingKpis || !teacherKpis ? (
            <div className="loading">Loading KPIs...</div>
          ) : (
            <div className="analytics-grid">
              {/* Reach KPIs */}
              <div className="analytics-card">
                <h3><span className="card-icon blue"><FaUsers /></span> Reach</h3>
                <div className="stat-row">
                  <div className="stat-item">
                    <span className="stat-label">Leagues</span>
                    <span className="stat-value">{teacherKpis.kpis.reach.leaguesManaged}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Students</span>
                    <span className="stat-value">{teacherKpis.kpis.reach.totalStudents}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Active (30d)</span>
                    <span className="stat-value">{teacherKpis.kpis.reach.activeStudents}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Engagement</span>
                    <span className="stat-value">{teacherKpis.kpis.reach.engagementRate}%</span>
                  </div>
                </div>
              </div>

              {/* Instruction KPIs */}
              <div className="analytics-card">
                <h3><span className="card-icon green"><FaBook /></span> Instruction</h3>
                <div className="stat-row">
                  <div className="stat-item">
                    <span className="stat-label">Lessons Assigned</span>
                    <span className="stat-value">{teacherKpis.kpis.instruction.lessonsAssigned}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Quizzes Assigned</span>
                    <span className="stat-value">{teacherKpis.kpis.instruction.quizzesAssigned}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Lessons Built</span>
                    <span className="stat-value">{teacherKpis.kpis.instruction.teacherLessonsCreated}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Quizzes Built</span>
                    <span className="stat-value">{teacherKpis.kpis.instruction.teacherQuizzesCreated}</span>
                  </div>
                </div>
              </div>

              {/* Outcomes KPIs */}
              <div className="analytics-card">
                <h3><span className="card-icon amber"><FaMedal /></span> Outcomes</h3>
                <div className="stat-row">
                  <div className="stat-item">
                    <span className="stat-label">Avg Level</span>
                    <span className="stat-value">{teacherKpis.kpis.outcomes.avgLevel}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Avg XP</span>
                    <span className="stat-value">{teacherKpis.kpis.outcomes.avgXp}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Avg Quiz</span>
                    <span className="stat-value">{teacherKpis.kpis.outcomes.avgQuizScore}%</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Completion</span>
                    <span className="stat-value">{teacherKpis.kpis.outcomes.lessonCompletionRate}%</span>
                  </div>
                </div>
              </div>

              {/* Portfolio KPIs */}
              <div className="analytics-card">
                <h3><span className="card-icon purple"><FaChartBar /></span> Portfolios</h3>
                <div className="stat-row">
                  <div className="stat-item">
                    <span className="stat-label">Portfolios</span>
                    <span className="stat-value">{teacherKpis.kpis.portfolio.portfolios}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Avg Value</span>
                    <span className="stat-value">${teacherKpis.kpis.portfolio.avgPortfolioValue}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Value</span>
                    <span className="stat-value">${teacherKpis.kpis.portfolio.totalPortfolioValue}</span>
                  </div>
                </div>
              </div>

              {/* Daily Activity Line Chart */}
              {teacherKpis.kpis.dailyActivity && teacherKpis.kpis.dailyActivity.length > 0 && (
                <div className="analytics-card full-width">
                  <h3><span className="card-icon cyan"><FaCalendar /></span> Daily Activity (Last 30 Days)</h3>
                  <div className="chart-container">
                    <Line
                      data={{
                        labels: teacherKpis.kpis.dailyActivity.map(d => {
                          const dt = new Date(d.date + 'T00:00:00');
                          return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                        }),
                        datasets: [
                          {
                            label: 'Lessons',
                            data: teacherKpis.kpis.dailyActivity.map(d => d.lessons),
                            borderColor: '#3b82f6',
                            backgroundColor: 'rgba(59,130,246,0.1)',
                            fill: true,
                            tension: 0.35,
                            pointRadius: 2,
                            pointHoverRadius: 5
                          },
                          {
                            label: 'Quizzes',
                            data: teacherKpis.kpis.dailyActivity.map(d => d.quizzes),
                            borderColor: '#f59e0b',
                            backgroundColor: 'rgba(245,158,11,0.1)',
                            fill: true,
                            tension: 0.35,
                            pointRadius: 2,
                            pointHoverRadius: 5
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        scales: {
                          y: { beginAtZero: true, ticks: { stepSize: 1, color: '#6b7280' }, grid: { color: '#f3f4f6' } },
                          x: { ticks: { maxRotation: 45, color: '#6b7280', maxTicksLimit: 15 }, grid: { display: false } }
                        },
                        plugins: { legend: { position: 'top', labels: { usePointStyle: true, padding: 16 } } }
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Per-league comparison */}
              {teacherKpis.kpis.leagueBreakdown && teacherKpis.kpis.leagueBreakdown.length > 0 && (
                <div className="analytics-card full-width">
                  <h3><span className="card-icon rose"><FaUsers /></span> Per-League Comparison</h3>
                  <div className="chart-container">
                    <Bar
                      data={{
                        labels: teacherKpis.kpis.leagueBreakdown.map(l => l.name),
                        datasets: [
                          {
                            label: 'Students',
                            data: teacherKpis.kpis.leagueBreakdown.map(l => l.students),
                            backgroundColor: 'rgba(59,130,246,0.7)',
                            borderRadius: 6
                          },
                          {
                            label: 'Active (30d)',
                            data: teacherKpis.kpis.leagueBreakdown.map(l => l.activeStudents),
                            backgroundColor: 'rgba(34,197,94,0.7)',
                            borderRadius: 6
                          },
                          {
                            label: 'Lessons Done',
                            data: teacherKpis.kpis.leagueBreakdown.map(l => l.lessonsCompleted),
                            backgroundColor: 'rgba(245,158,11,0.7)',
                            borderRadius: 6
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: { beginAtZero: true, ticks: { color: '#6b7280' }, grid: { color: '#f3f4f6' } },
                          x: { ticks: { color: '#6b7280' }, grid: { display: false } }
                        },
                        plugins: { legend: { position: 'top', labels: { usePointStyle: true, padding: 16 } } }
                      }}
                    />
                  </div>
                </div>
              )}

              {/* League breakdown table */}
              {teacherKpis.kpis.leagueBreakdown && teacherKpis.kpis.leagueBreakdown.length > 0 && (
                <div className="analytics-card full-width">
                  <h3><span className="card-icon amber"><FaTrophy /></span> League Breakdown</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="student-ranking-table">
                      <thead>
                        <tr>
                          <th>League</th>
                          <th>Students</th>
                          <th>Active</th>
                          <th>Avg Level</th>
                          <th>Avg XP</th>
                          <th>Lessons</th>
                          <th>Quiz Attempts</th>
                          <th>Avg Quiz</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teacherKpis.kpis.leagueBreakdown.map((l) => (
                          <tr key={l.id}>
                            <td style={{ fontWeight: 600 }}>{l.name}</td>
                            <td>{l.students}</td>
                            <td>{l.activeStudents}</td>
                            <td>{l.avgLevel}</td>
                            <td>{l.avgXp}</td>
                            <td>{l.lessonsCompleted}</td>
                            <td>{l.quizAttempts}</td>
                            <td>{l.avgQuizScore}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default LeagueManagement;
